import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ArrowRight, Workflow, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { LineChart, Line, ResponsiveContainer, YAxis } from 'recharts';
import { STAGE_META, stageOf } from '../../lib/journeySignals';

const NODE_STAGES = [
  { stage: 'capability', label: 'Capability Signal' },
  { stage: 'confidence', label: 'Confidence Signal' },
  { stage: 'coordination', label: 'Coordination Signal' },
];
const DEFAULTS = { capability: 'skills_coverage', confidence: 'training_performance', coordination: 'scheduling_velocity' };

function ChangeBadge({ pct }) {
  if (pct == null) return <span className="text-xs text-slate-400">—</span>;
  const Icon = pct > 0 ? TrendingUp : pct < 0 ? TrendingDown : Minus;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold tabular-nums ${
      pct > 0 ? 'text-emerald-600' : pct < 0 ? 'text-rose-600' : 'text-slate-500'
    }`}>
      <Icon className="h-3.5 w-3.5" />{pct > 0 ? '+' : ''}{pct.toFixed(1)}%
    </span>
  );
}

function Sparkline({ points, color }) {
  if (!points?.length) return <div className="h-12 flex items-center justify-center text-[10px] text-slate-400">no data</div>;
  return (
    <div className="h-12">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={points}>
          <YAxis hide domain={['dataMin', 'dataMax']} />
          <Line type="monotone" dataKey="y" stroke={color} strokeWidth={2} dot={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

/**
 * Signal → Outcome — chains a representative signal from each journey
 * stage into a business KPI: all existing trend data, new narrative.
 * Capability → Confidence → Coordination → Outcome (KPI).
 */
export function SignalToOutcomeTab({ trendsData, kpiTrendsData }) {
  const measures = trendsData?.trends || {};
  const kpis = kpiTrendsData?.by_kpi || {};

  const optionsFor = (stage) =>
    Object.entries(measures)
      .filter(([id]) => stageOf(id) === stage)
      .map(([id, m]) => ({ id, name: m.measure_name }))
      .sort((a, b) => a.name.localeCompare(b.name));

  const [selected, setSelected] = useState(DEFAULTS);
  const [outcomeKpi, setOutcomeKpi] = useState(null);

  // Keep defaults valid against the loaded data
  useEffect(() => {
    setSelected((prev) => {
      const next = { ...prev };
      NODE_STAGES.forEach(({ stage }) => {
        const opts = optionsFor(stage);
        if (opts.length && !opts.some((o) => o.id === next[stage])) next[stage] = opts[0].id;
      });
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trendsData]);
  useEffect(() => {
    const ids = Object.keys(kpis);
    if (ids.length && (!outcomeKpi || !ids.includes(outcomeKpi))) {
      setOutcomeKpi(ids.includes('csat') ? 'csat' : ids[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kpiTrendsData]);

  const nodes = useMemo(() => {
    const signalNode = ({ stage, label }) => {
      const id = selected[stage];
      const m = measures[id];
      const points = (m?.data_points || []).map((dp) => ({ y: dp.calculated_score }));
      return {
        key: stage, label, color: STAGE_META[stage].color, options: optionsFor(stage),
        selectedId: id, onSelect: (v) => setSelected((p) => ({ ...p, [stage]: v })),
        latest: m?.trend?.latest_score, changePct: m?.trend?.change_percent, points,
      };
    };
    const k = kpis[outcomeKpi];
    const kpiPoints = (k?.data_points || []).map((dp) => ({ y: dp.value }));
    const kpiChangePct = k?.data_points?.length > 1 && k.data_points[0].value
      ? ((k.data_points[k.data_points.length - 1].value - k.data_points[0].value) / Math.abs(k.data_points[0].value)) * 100
      : null;
    return [
      ...NODE_STAGES.map(signalNode),
      {
        key: 'outcome', label: 'Business Outcome', color: '#e11d48',
        options: Object.entries(kpis).map(([id, d]) => ({ id, name: d.kpi_name })),
        selectedId: outcomeKpi, onSelect: setOutcomeKpi,
        latest: k?.data_points?.length ? k.data_points[k.data_points.length - 1].value : null,
        changePct: kpiChangePct, points: kpiPoints, unit: k?.unit,
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [measures, kpis, selected, outcomeKpi]);

  const hasAnyData = Object.keys(measures).length > 0;

  return (
    <Card data-testid="signal-to-outcome-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Workflow className="h-5 w-5 text-cyan-700" />
          Signal → Outcome
        </CardTitle>
        <p className="text-xs text-slate-500">
          How learning signals propagate into business outcomes — pick a signal for each journey stage and watch the chain. All existing data, new narrative.
        </p>
      </CardHeader>
      <CardContent>
        {hasAnyData ? (
          <div className="flex flex-col xl:flex-row items-stretch gap-2">
            {nodes.map((n, i) => (
              <React.Fragment key={n.key}>
                {i > 0 && (
                  <div className="flex items-center justify-center flex-shrink-0 py-1 xl:py-0">
                    <ArrowRight className="h-5 w-5 text-slate-300 rotate-90 xl:rotate-0" />
                  </div>
                )}
                <div className="flex-1 rounded-lg border border-slate-200 bg-slate-50/60 p-3" data-testid={`sto-node-${n.key}`}>
                  <span
                    className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold text-white mb-2"
                    style={{ backgroundColor: n.color }}
                  >
                    {n.label}
                  </span>
                  <Select value={n.selectedId || ''} onValueChange={n.onSelect}>
                    <SelectTrigger className="h-8 text-xs bg-white" data-testid={`sto-select-${n.key}`}>
                      <SelectValue placeholder="Select…" />
                    </SelectTrigger>
                    <SelectContent>
                      {n.options.map((o) => <SelectItem key={o.id} value={o.id} className="text-xs">{o.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Sparkline points={n.points} color={n.color} />
                  <div className="flex items-baseline justify-between">
                    <p className="text-xl font-extrabold text-slate-900 tabular-nums">
                      {n.latest != null ? `${Number(n.latest).toLocaleString(undefined, { maximumFractionDigits: 1 })}${n.key === 'outcome' ? (n.unit === '%' ? '%' : '') : ''}` : '—'}
                    </p>
                    <ChangeBadge pct={n.changePct} />
                  </div>
                </div>
              </React.Fragment>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 text-slate-500">
            <Workflow className="h-12 w-12 text-slate-300 mx-auto mb-3" />
            <p>No signal data yet — import LEI baselines to build the chain.</p>
          </div>
        )}
        <p className="mt-3 text-[11px] text-slate-400">
          Reading the chain: a healthy journey shows gains propagating left to right. A strong signal that fails to lift the next stage marks where the Missing Middle is leaking value.
        </p>
      </CardContent>
    </Card>
  );
}
